import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Pencil, Trash2, Plus, Loader2, Lock } from 'lucide-react';
import { cn } from '../../lib/utils';
import { kbCategoriesAPI } from '../../lib/api';

// Must mirror backend ALLOWED_KB_COLORS and KB_COLOR_MAP in KnowledgeBase.jsx
const COLORS = [
  { value: 'blue',    sw: 'bg-blue-500' },
  { value: 'emerald', sw: 'bg-emerald-500' },
  { value: 'purple',  sw: 'bg-purple-500' },
  { value: 'amber',   sw: 'bg-amber-500' },
  { value: 'rose',    sw: 'bg-rose-500' },
  { value: 'indigo',  sw: 'bg-indigo-500' },
  { value: 'teal',    sw: 'bg-teal-500' },
  { value: 'lime',    sw: 'bg-lime-500' },
  { value: 'orange',  sw: 'bg-orange-500' },
  { value: 'slate',   sw: 'bg-slate-500' },
  { value: 'cyan',    sw: 'bg-cyan-500' },
  { value: 'pink',    sw: 'bg-pink-500' },
];

const ColorPicker = ({ value, onChange }) => (
  <div className="flex flex-wrap gap-1.5" data-testid="kb-color-picker">
    {COLORS.map(c => (
      <button
        key={c.value}
        type="button"
        onClick={() => onChange(c.value)}
        className={cn(
          'w-7 h-7 rounded-full transition-all',
          c.sw,
          value === c.value
            ? 'ring-2 ring-offset-2 ring-slate-900 scale-110'
            : 'opacity-70 hover:opacity-100'
        )}
        title={c.value}
        data-testid={`kb-color-${c.value}`}
      />
    ))}
  </div>
);

const KBCategoryManagerDialog = ({ open, onOpenChange, categories, onChanged }) => {
  const [items, setItems] = useState([]);
  const [draftValue, setDraftValue] = useState('');
  const [draftColor, setDraftColor] = useState('blue');
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [editColor, setEditColor] = useState('slate');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setItems(categories || []);
  }, [open, categories]);

  const sorted = useMemo(
    () => [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [items]
  );

  const refresh = async () => {
    try {
      const res = await kbCategoriesAPI.getAll();
      setItems(res.data || []);
      onChanged?.(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const handleCreate = async () => {
    const v = draftValue.trim();
    if (!v) {
      toast.error('Escribe un nombre');
      return;
    }
    setBusy(true);
    try {
      await kbCategoriesAPI.create({ value: v, color: draftColor });
      toast.success('Categoría agregada');
      setDraftValue('');
      setDraftColor('blue');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo crear');
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (cat) => {
    setEditingId(cat.id);
    setEditLabel(cat.label);
    setEditColor(cat.color);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditLabel('');
  };

  const handleSaveEdit = async (id) => {
    setBusy(true);
    try {
      await kbCategoriesAPI.update(id, { label: editLabel, color: editColor });
      toast.success('Categoría actualizada');
      setEditingId(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo actualizar');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`¿Eliminar la categoría "${cat.label}"?`)) return;
    setBusy(true);
    try {
      await kbCategoriesAPI.delete(cat.id);
      toast.success('Categoría eliminada');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="kb-manage-categories-dialog">
        <DialogHeader>
          <DialogTitle>Gestionar categorías</DialogTitle>
        </DialogHeader>

        {/* Create new */}
        <div className="rounded-lg border border-slate-200 p-4 space-y-3">
          <Label className="text-sm font-medium text-slate-700">Nueva categoría</Label>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              value={draftValue}
              onChange={(e) => setDraftValue(e.target.value)}
              placeholder="Ej. Procesos, Capacitación..."
              maxLength={60}
              data-testid="kb-new-cat-value"
            />
          </div>
          <ColorPicker value={draftColor} onChange={setDraftColor} />
          <div className="flex justify-end">
            <Button onClick={handleCreate} disabled={busy} data-testid="kb-create-cat-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Agregar
            </Button>
          </div>
        </div>

        {/* List */}
        <div className="space-y-2">
          {sorted.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-4">No hay categorías</p>
          )}
          {sorted.map(cat => (
            <div
              key={cat.id}
              className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2"
              data-testid={`kb-cat-row-${cat.value}`}
            >
              {editingId === cat.id ? (
                <>
                  <div className={cn('w-3 h-3 rounded-full flex-shrink-0', COLORS.find(c => c.value === editColor)?.sw || 'bg-slate-500')} />
                  <Input
                    value={editLabel}
                    onChange={(e) => setEditLabel(e.target.value)}
                    className="flex-1"
                    autoFocus
                    data-testid={`kb-edit-label-${cat.value}`}
                  />
                  <ColorPicker value={editColor} onChange={setEditColor} />
                  <Button size="sm" onClick={() => handleSaveEdit(cat.id)} disabled={busy} data-testid={`kb-save-cat-${cat.value}`}>
                    Guardar
                  </Button>
                  <Button size="sm" variant="ghost" onClick={cancelEdit}>
                    Cancelar
                  </Button>
                </>
              ) : (
                <>
                  <div className={cn('w-3 h-3 rounded-full flex-shrink-0', COLORS.find(c => c.value === cat.color)?.sw || 'bg-slate-500')} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-slate-800 truncate">{cat.label}</div>
                    <div className="text-xs text-slate-400 truncate">valor: {cat.value}</div>
                  </div>
                  {cat.is_default && (
                    <span className="text-[10px] text-slate-500 flex items-center gap-1">
                      <Lock className="w-3 h-3" /> default
                    </span>
                  )}
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => startEdit(cat)}
                    data-testid={`kb-edit-cat-${cat.value}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => handleDelete(cat)}
                    data-testid={`kb-delete-cat-${cat.value}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </>
              )}
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default KBCategoryManagerDialog;
